import { useState, type JSX } from 'react';
import {
  ShoppingCart,
  Package,
  AlertTriangle,
  Upload,
  User,
  Truck,
  Flag,
  CheckCircle,
  Activity,
  type LucideIcon,
} from 'lucide-react';
import clsx from 'clsx';

// ── Activity type icons ─────────────────────────────────────────────────────

const TYPE_ICONS: Record<string, LucideIcon> = {
  po_created: ShoppingCart,
  po_approved: CheckCircle,
  po_received: Package,
  product_flagged: Flag,
  alert_triggered: AlertTriangle,
  alert_resolved: CheckCircle,
  import_complete: Upload,
  user_action: User,
  order_status: Truck,
};

const TYPE_COLORS: Record<string, string> = {
  po_created: 'text-accent',
  po_approved: 'text-success',
  po_received: 'text-success',
  product_flagged: 'text-warning',
  alert_triggered: 'text-warning',
  alert_resolved: 'text-success',
  import_complete: 'text-info',
  user_action: 'text-ink-muted',
  order_status: 'text-info',
};

// ── Filter tabs ─────────────────────────────────────────────────────────────

type FilterId = 'today' | 'this_week' | 'all';

const FILTERS: { id: FilterId; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'this_week', label: 'This Week' },
  { id: 'all', label: 'All' },
];

// ── Types ───────────────────────────────────────────────────────────────────

export interface ActivityItem {
  id?: string | number;
  type?: string;
  description: string;
  created_at: string;
  action_url?: string | null;
}

interface ActivityFeedProps {
  activities?: ActivityItem[];
}

// ── Group by day ────────────────────────────────────────────────────────────

function groupByDay(items: ActivityItem[]): [string, ActivityItem[]][] {
  const groups: Record<string, ActivityItem[]> = {};
  const today = new Date().toDateString();
  const yesterday = new Date(Date.now() - 86400000).toDateString();

  for (const item of items) {
    const d = new Date(item.created_at).toDateString();
    const label = d === today ? 'Today' : d === yesterday ? 'Yesterday' : d;
    if (!groups[label]) groups[label] = [];
    groups[label].push(item);
  }
  return Object.entries(groups);
}

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ── ActivityFeed ────────────────────────────────────────────────────────────

export default function ActivityFeed({ activities = [] }: ActivityFeedProps): JSX.Element {
  const [filter, setFilter] = useState<FilterId>('today');

  const grouped = groupByDay(activities);
  const hasItems = activities.length > 0;

  return (
    <div className="arbi-card flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
        <h3 className="text-xs font-medium text-ink-secondary uppercase tracking-wider">
          Activity
        </h3>
        <div className="flex items-center bg-surface-3 rounded-md p-0.5 gap-0.5 border border-border-subtle">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={clsx(
                'px-2 py-0.5 text-2xs font-medium rounded transition-colors cursor-pointer',
                filter === f.id
                  ? 'bg-surface-5 text-ink-bright'
                  : 'text-ink-muted hover:text-ink-secondary'
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="overflow-y-auto pr-2.5" style={{ maxHeight: 400 }}>
        {hasItems ? (
          <div>
            {grouped.map(([dayLabel, items]) => (
              <div key={dayLabel}>
                <div className="px-4 py-1.5 text-2xs text-ink-muted font-medium uppercase tracking-wider bg-surface-2">
                  {dayLabel}
                </div>
                {items.map((item, i) => {
                  const Icon = (item.type && TYPE_ICONS[item.type]) || Package;
                  return (
                    <div
                      key={item.id ?? i}
                      className="flex items-center gap-3 px-4 py-2 hover:bg-surface-3 transition-colors"
                    >
                      <Icon
                        size={14}
                        className={clsx(
                          (item.type && TYPE_COLORS[item.type]) || 'text-ink-muted',
                          'flex-shrink-0'
                        )}
                      />
                      <span className="flex-1 text-xs text-ink-primary">{item.description}</span>
                      <span className="text-2xs font-mono text-ink-muted flex-shrink-0">
                        {relativeTime(item.created_at)}
                      </span>
                      {item.action_url && (
                        <button className="text-2xs text-accent hover:text-accent-hover font-medium flex-shrink-0">
                          View
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 gap-2">
            <Activity size={24} className="text-ink-muted opacity-40" />
            <p className="text-xs text-ink-muted">No recent activity</p>
            <p className="text-2xs text-ink-muted opacity-60">
              Actions across your account will appear here
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
